import { useMemo } from 'react';
import type { LocalItemDto } from '../lib/types';

/**
 * The per-line Item cell on the Purchase Invoice review screen: a plain searchable lookup over the
 * connected company's item (Product/Service) master. Sibling of LineAccountPicker, deliberately
 * simpler: there is no iERP AI suggestion for items, just a chart to pick from.
 *
 * THE LIST IS THE ITEM MASTER, NOT A TEXT BOX — same reasoning as the Account picker: a reviewer who
 * cannot name every item id by heart can still verify or correct a choice, because the alternatives
 * are right there. Only when no item master has been pulled yet does the cell fall back to the old
 * free-text id, so that screen never gets worse than it was. A "Category" item is a non-postable
 * QuickBooks grouping node, so it never appears here even though the backend cache retains it.
 */
export function itemMatchesRole(item: LocalItemDto): boolean {
  return item.active && item.missingSinceUtc === null && item.isEligibleForNewPosting;
}

export function itemOptionLabel(item: LocalItemDto): string {
  const type = item.type ? ` — ${item.type}` : '';
  return `${item.name}${type}`;
}

/** The items a purchase line may be coded to, narrowed by the shared filter text. */
export function filterLineItems(items: LocalItemDto[], filter: string): LocalItemDto[] {
  const term = filter.trim().toLowerCase();
  return items
    .filter(itemMatchesRole)
    .filter((item) => {
      if (!term) return true;
      return [item.name, item.fullyQualifiedName, item.type]
        .some((field) => (field ?? '').toLowerCase().includes(term));
    });
}

const MAX_OPTIONS = 200;

export interface LineItemPickerProps {
  lineNo: number;
  /** The reviewer's coded item id; null means no item is coded on this line. */
  value: string | null;
  /** The connected company's eligible items (learningItems.allEligible). */
  items: LocalItemDto[];
  itemsLoading: boolean;
  /** Filter text shared by every line's picker, so one search narrows the whole table. */
  filter: string;
  readOnly: boolean;
  onChange: (itemId: string | null) => void;
}

export function LineItemPicker({
  lineNo,
  value,
  items,
  itemsLoading,
  filter,
  readOnly,
  onChange,
}: LineItemPickerProps): JSX.Element {
  const byId = useMemo(() => new Map(items.map((item) => [item.externalItemId, item])), [items]);
  const matching = useMemo(() => filterLineItems(items, filter), [items, filter]);

  // No item master to choose from yet: keep a free-text id so the screen never regresses.
  if (!itemsLoading && items.length === 0) {
    return (
      <input
        className="input input-inline"
        value={value ?? ''}
        readOnly={readOnly}
        placeholder="QuickBooks item id"
        aria-label={`Line ${lineNo} QuickBooks item id`}
        onChange={(event) => onChange(event.target.value || null)}
      />
    );
  }

  const visible = matching.slice(0, MAX_OPTIONS);

  // A saved coding that is no longer in the filtered list (or no longer in the master) stays
  // selectable, so opening the screen never silently loses a choice somebody already made.
  const currentItem = value ? byId.get(value) : undefined;
  const keepCurrent = value !== null && !visible.some((item) => item.externalItemId === value);

  const masterLabel =
    matching.length > MAX_OPTIONS
      ? `Item master (first ${MAX_OPTIONS} of ${matching.length}; filter to narrow)`
      : 'Item master';

  return (
    <select
      className="input input-inline select"
      value={value ?? ''}
      disabled={readOnly || itemsLoading}
      aria-label={`Line ${lineNo} QuickBooks item`}
      onChange={(event) => onChange(event.target.value || null)}
    >
      <option value="">{itemsLoading ? 'Loading items…' : 'Choose an item'}</option>
      {keepCurrent ? (
        <option value={value!}>
          {currentItem ? itemOptionLabel(currentItem) : value} — current selection
        </option>
      ) : null}
      <optgroup label={masterLabel}>
        {visible.map((item) => (
          <option key={item.externalItemId} value={item.externalItemId}>
            {itemOptionLabel(item)}
          </option>
        ))}
      </optgroup>
    </select>
  );
}
